/** Parent registration detail tab. */
import * as React from 'react';
import { CheckCircle2, User, MapPin, FileCheck, Eye, Download } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { Collapsible } from '@/components/ui/collapsible';
import { Button } from '@/components/forms/Button';
import {
  PARENT_STEP_LABELS,
  getProfessionalSituationLabel,
  YES_NO_BADGE_CONFIG,
  STEP_COMPLETION_CONFIG,
} from '@/components/parents/parent-configs';
import type { ParentRecord } from '@/types/parent.types';
import {
  capitalizeFirst,
  daysSince,
  downloadViaBlob,
  formatCityInfo,
  formatDate,
  formatDepartmentValue,
  getCivilTitleLabel,
  getCompletionStatus,
  getContractValidKind,
  getParentProfessionalSituationFields,
  getSectionCompletionPercent,
  openDocumentPreview,
} from '@/components/parents/parent-table-utils';

/** Badge for contract validity. */
function ContractValidBadge({ isValid }: Readonly<{ isValid: unknown }>) {
  const kind = getContractValidKind(isValid);
  if (kind === 'yes') {
    return (
      <LabelBadge
        label="Oui"
        icon={CheckCircle2}
        variant="solid"
        color="rgb(34 197 94)"
        size="sm"
      />
    );
  }
  if (kind === 'no') {
    return (
      <LabelBadge
        label="Non"
        icon={CheckCircle2}
        variant="outlined"
        color="rgb(239 68 68)"
        size="sm"
      />
    );
  }
  return '—';
}

/** Outlined badge for a yes/no value. */
function YesNoBadge({ value }: Readonly<{ value: string | undefined }>) {
  if (!value) return null;
  const config = YES_NO_BADGE_CONFIG[value];
  if (!config) return <span className="text-sm text-foreground">{value}</span>;
  return (
    <LabelBadge
      label={config.label}
      icon={config.icon}
      variant="outlined"
      color={config.color}
      size="sm"
    />
  );
}

/** Flattened professional-situation detail fields. */
function ProfessionalSituationFields({
  professionalSituation,
  departments,
}: Readonly<{
  professionalSituation: unknown;
  departments: { code: string; name: string }[];
}>) {
  const fields = getParentProfessionalSituationFields(professionalSituation, departments);
  if (!fields) return null;
  return (
    <>
      {fields.map((field) => (
        <DetailField
          key={field.key}
          label={field.label}
          value={field.kind === 'yesNo' ? <YesNoBadge value={field.value} /> : field.value}
        />
      ))}
    </>
  );
}

/** Onboarding template used for step completion. */
interface Template {
  key: string;
  displayName: string;
  sortOrder: number;
}

/** One onboarding step and its completion date. */
interface Step {
  stepKey: string;
  completedAt?: string | null;
}

/** Props for {@link ParentRegistrationTab}. */
interface ParentRegistrationTabProps {
  record: ParentRecord;
  sections: Record<string, unknown>;
  departments: { code: string; name: string }[];
  templates?: Template[];
  steps?: Step[];
}

/** Parent registration detail tab. */
export const ParentRegistrationTab: React.FC<ParentRegistrationTabProps> = ({
  record,
  sections,
  departments,
  templates,
  steps,
}) => (
  <div className="details-view-grid mt-6">
    <div className="section-card-full">
      <Collapsible
        title="Suivi inscription"
        subtitle={`Inscrit le ${formatDate(record.createdAt)} (${daysSince(record.createdAt)})`}
        defaultOpen
      >
        <div className="space-y-4">
          <div className="border-l-2 border-black pl-4 space-y-2">
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3">
              Inscription
            </h4>
            {templates
              ?.slice()
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((tpl) => {
                const sectionData = sections[tpl.key] as any;
                const pct = getSectionCompletionPercent(sectionData);
                const stepProgress = steps?.find((s) => s.stepKey === tpl.key);
                const completionStatus = getCompletionStatus(pct);
                const completionConfig = STEP_COMPLETION_CONFIG[completionStatus];
                return (
                  <div key={tpl.key} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">
                      {PARENT_STEP_LABELS[tpl.key] ?? tpl.displayName}
                    </span>
                    <div className="flex items-center gap-3">
                      <LabelBadge
                        label={completionConfig.label}
                        icon={completionConfig.icon}
                        variant={completionConfig.badgeVariant}
                        color={completionConfig.badgeColor}
                        size="sm"
                      />
                      <span className="text-xs text-muted-foreground min-w-[70px] text-right">
                        {stepProgress?.completedAt ? formatDate(stepProgress.completedAt) : '—'}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </Collapsible>
    </div>

    <DetailsCard icon={User} title="Informations de base" colorName="color-2" colSpan={1}>
      {(() => {
        const d = sections['basic_information'] as any;
        const pct = getSectionCompletionPercent(d);
        const completionConfig = STEP_COMPLETION_CONFIG[getCompletionStatus(pct)];
        return (
          <>
            <div className="flex items-center justify-between mb-3">
              <LabelBadge
                label={`${pct}%`}
                icon={completionConfig.icon}
                variant={completionConfig.badgeVariant}
                color={completionConfig.badgeColor}
                size="sm"
              />
            </div>
            {d ? (
              <div className="space-y-3">
                <DetailField
                  label="Nom / Prénom"
                  value={`${d.firstName ?? '—'} ${d.lastName ?? '—'}`}
                />
                <DetailField label="Email" value={d.email} />
                <DetailField label="Téléphone" value={d.phoneNumber} />
                <DetailField label="Civilité" value={getCivilTitleLabel(d.gender)} />
                {d.loginCode && <DetailField label="Code de connexion" value={d.loginCode} />}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Aucune donnée</p>
            )}
          </>
        );
      })()}
    </DetailsCard>

    <DetailsCard icon={MapPin} title="Informations personnelles" colorName="color-1" colSpan={1}>
      {(() => {
        const d = sections['personal_information'] as any;
        const pct = getSectionCompletionPercent(d);
        const completionConfig = STEP_COMPLETION_CONFIG[getCompletionStatus(pct)];
        return (
          <>
            <div className="flex items-center justify-between mb-3">
              <LabelBadge
                label={`${pct}%`}
                icon={completionConfig.icon}
                variant={completionConfig.badgeVariant}
                color={completionConfig.badgeColor}
                size="sm"
              />
            </div>
            {d ? (
              <div className="space-y-3">
                <DetailField label="Adresse" value={d.address} />
                <DetailField label="Ville" value={formatCityInfo(d.zipCode, d.city)} />
                <DetailField label="Pays" value={capitalizeFirst(d.country)} />
                <DetailField
                  label="Département"
                  value={formatDepartmentValue(d.department, departments)}
                />
                <DetailField label="Date de naissance" value={formatDate(d.dateOfBirth)} />
                <DetailField
                  label="Situation professionnelle"
                  value={getProfessionalSituationLabel(d.professionalSituation)}
                />
                <ProfessionalSituationFields
                  professionalSituation={d.professionalSituation}
                  departments={departments}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Aucune donnée</p>
            )}
          </>
        );
      })()}
    </DetailsCard>

    <DetailsCard icon={FileCheck} title="Contrat" colorName="color-3" colSpan={2}>
      {(() => {
        const d = sections['contract'] as any;
        const pct = getSectionCompletionPercent(d);
        const completionConfig = STEP_COMPLETION_CONFIG[getCompletionStatus(pct)];
        return (
          <>
            <div className="flex items-center justify-between mb-3">
              <LabelBadge
                label={`${pct}%`}
                icon={completionConfig.icon}
                variant={completionConfig.badgeVariant}
                color={completionConfig.badgeColor}
                size="sm"
              />
            </div>
            {d ? (
              <div className="space-y-3">
                <DetailField
                  label="Contrat valide"
                  value={<ContractValidBadge isValid={d.isValid} />}
                />
                <DetailField label="Date de signature" value={formatDate(d.signedDate)} />
                {d.documentLink ? (
                  <DetailField
                    label="Document"
                    value={
                      <div className="flex gap-1 flex-wrap">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void openDocumentPreview(d.documentLink)}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" />
                          Aperçu
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            downloadViaBlob(d.documentLink, d.documentName ?? 'contrat.pdf')
                          }
                        >
                          <Download className="w-3.5 h-3.5 mr-1" />
                          Télécharger
                        </Button>
                      </div>
                    }
                  />
                ) : (
                  <DetailField label="Document" value="—" />
                )}
                {d.uploadDate && (
                  <DetailField label="Date d'upload" value={formatDate(d.uploadDate)} />
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Aucune donnée de contrat.</p>
            )}
          </>
        );
      })()}
    </DetailsCard>
  </div>
);
